import axios from 'axios'
import { createClient } from 'graphqurl'
import { useRouter } from 'next/router'
import { useContext, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import useSWR from 'swr'
import * as Yup from 'yup'
import { faCircleInfo } from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import moment from 'moment'
import {
	Button,
	Card,
	Col,
	Image,
	Modal,
	Row
} from 'react-bootstrap'

import styles from '../../styles/Register.module.scss'
import techTalentStyles from '../../styles/TechTalent.module.scss'
import UserContext from '../../UserContext'
import PersonalDetails from './forms/PersonalDetails'
import WorkExperience from './forms/WorkExperience'
import AvailabilityStatus from './forms/AvailabilityStatus'

const swrKey = `
query ($id: Int!) {
  Applicant_by_pk(id: $id) {
    name
    contact
    email
    gender
    dateOfBirth
		locationMrt
    nationality
    workPass
    whatsapp
    educationLevel
    education
    isEmployed
    unemployedDuration
    isStartWorkNow
    broadcastStartDate
    broadcastEndDate
    # password
    avatar
    cvName
    cv
		locationCity
		nric
  }
}
`

export default function ProfileSteps({ isNewProfile, token }): JSX.Element {
	const { t } = useTranslation()
	const router = useRouter()
	const source = router.query.source
	const locale = router?.locale?.toLocaleUpperCase()
	const [schema, setSchema] = useState(Yup.object().shape({}))

	// Columns(Applicant)
	const [inputs, setInputs] = useState({
		name: '',
		email: '',
		nric: '',
		gender: '',
		dateOfBirth: '',
		locationMrt: '',
		nationality: '',
		workPass: '',
		whatsapp: '',
		educationLevel: '',
		education: '',
		isEmployed: false,
		unemployedDuration: '',
		isStartWorkNow: false,
		password: '',
		// update
		avatar: '',
		cvName: '',
		cv: '',
		broadcastStartDate: null as string | null,
		broadcastEndDate: null as string | null,
		locationCity: null,
	})
	const [confirmPw, setConfirmPw] = useState('')

	// singpass myinfo status state
	const [isMyinfoSuccess, setIsMyinfoSuccess] = useState(false)
	const [isMyinfoFail, setIsMyinfoFail] = useState(false)
	// state for singpass prompt modal
	const [showSingpassPrompt, setShowSingpassPrompt] = useState(true)

	// for <Button /> state
	const [boolean, setBoolean] = useState({
		gender: '',
		isSingaporean: '',
		currentlyEmployed: '',
		startWorkImmediately: '',
		broadcastToEmployers: '',
		isLocale: '',
	})

	const [imgPreview, setImgPreview] = useState('')

	const [imgData, setImgData] = useState<File | undefined>(undefined)
	const [cvData, setCVData] = useState<File | undefined>(undefined)

	const { user } = useContext(UserContext)

	const fetcher = async (url, hasuraQuery): Promise<any> => {
		const res = await axios.get(url)

		if (res.status === 200) {
			// '/update-profile'
			if (isNewProfile === false) {
				const token = window.localStorage.getItem('token')
				try {
					const client = createClient({
						endpoint: process.env.NEXT_PUBLIC_GRAPHQL_ENDPOINT as string,
						headers: { authorization: token !== null ? `Bearer ${token}` : '' },
					})
					const hasuraRes = await client.query({
						query: hasuraQuery,
						variables: { id: user.id },
					})
					const { data } = hasuraRes
					const applicantData = data.Applicant_by_pk
					const handleCVTextInput = (): any => {
						if (applicantData.cv !== null && applicantData.cv !== '') {
							return applicantData.cvName !== null &&
                                applicantData.cvName !== ''
								? applicantData.cvName
								: '1 file selected'
						}
						return 'No file selected'
					}
					applicantData.cvName = handleCVTextInput()
					applicantData.locationMrt =
                        applicantData.locationMrt === null ||
                            applicantData.locationMrt === 'null'
                        	? null
                        	: applicantData.locationMrt
					applicantData.locationCity =
                        applicantData.locationCity === null ||
                            applicantData.locationCity === 'null'
                        	? null
                        	: applicantData.locationCity
					applicantData.workPass =
                        applicantData.workPass === null || applicantData.workPass === 'null'
                        	? null
                        	: applicantData.workPass
					applicantData.unemployedDuration =
                        applicantData.unemployedDuration === null
                        	? ''
                        	: applicantData.unemployedDuration
					setInputs(applicantData)
					setBoolean({
						gender: applicantData.gender === 'MALE' ? 'male' : applicantData.gender === 'FEMALE' ? 'female' : '',
						isSingaporean:
                            applicantData.nationality === 'SG' ? 'true' : 'false',
						currentlyEmployed:
                            applicantData.isEmployed === true ? 'true' : 'false',
						startWorkImmediately:
                            applicantData.isStartWorkNow === true ? 'true' : 'false',
						broadcastToEmployers:
                            applicantData.broadcastStartDate !== null ? 'true' : 'false',
						isLocale: applicantData.nationality === 'SG' ? 'true' : 'false',
					})
				} catch (err) {
					// console.error(err)
				}
			}
			if (source === 'broadcast') {
				setBoolean({ ...boolean, broadcastToEmployers: 'true' })
				setInputs({
					...inputs,
					broadcastStartDate: moment().format(),
					broadcastEndDate: moment().add(1, 'years').format(),
				})
			}
			// else {
			//   // Only returning data for selects
			//   return res.data
			// }
			// Regardless of the "source", we only need the datas for select
			return res.data
		}
	}

	const { data, error, isValidating } = useSWR(
		[process.env.NEXT_PUBLIC_FETCH_OPTIONS as string, swrKey],
		fetcher,
		{ revalidateOnFocus: false }
	)
	// if (error !== undefined) return <div>Failed to load</div>
	// if (data === undefined) return <div>Loading...</div>

	useEffect(() => {
		const fetchProfile = async () => {
			await axios
				.get(`/api/singpass-auth/myinfo?token=${router.query.token}`)
				.then((response) => {
					const personData = response.data?.personData ?? null
					if (personData !== null) {
						setInputs((prevInputs) => ({
							...prevInputs,
							nric: personData.uinfin.value,
							email: personData.email?.value ?? '',
							name: personData.name.value,
							gender: personData.sex.desc,
							dateOfBirth: personData.dob.value,
							nationality: personData.nationality.code,
							whatsapp: `${personData.mobileno.prefix.value ?? ''}${personData.mobileno.areacode.value ?? ''
							}${personData.mobileno.nbr.value ?? ''}`,
						}))
						setBoolean((prevBoolean) => ({
							...prevBoolean,
							gender: personData.sex.desc ?? '',
							isLocale:
                                personData.nationality.code === locale ? 'true' : 'false',
						}))
						setIsMyinfoSuccess(true)
					}
				})
				.catch(() => {
					setIsMyinfoFail(true)
				})
		}
		if (token && !isValidating) fetchProfile()
	}, [token, isValidating, locale, router.query.token])

	if (error !== undefined) return <div>Failed to load</div>
	if (data === undefined) return <div>Loading...</div>

	return (
		<>
			<Card>
				<Card.Body className={styles.profileCard}>
					<Row className="w-100 align-items-center">
						<Col>
							<h1 className={styles.profileHeader}>
								{isNewProfile === true
									? t('update_profile.create')
									: t('update_profile.update')}{' '}
								{t('update_profile.my_profile')}
							</h1>
						</Col>
						<Col md={4}>
							<Button
								className={`text ${styles.singpass}`}
								variant="outline"
								type="button"
								href={`/api/singpass-auth/myinfo-login?type=${isNewProfile ? 'register' : 'update'}`}
							>
								<strong>Retrieve Myinfo with{' '}</strong>
								<Image
									src="https://portal.singpass.gov.sg/home/ui/static/media/singpass-logo.bdceeae10dd5834331ee450d84f50e8f.svg"
									height={16}
									alt="Retrieve Myinfo with sing pass"
									className='mt-1'
								/>
							</Button>
						</Col>
					</Row>

					<PersonalDetails
						inputs={inputs}
						setInputs={setInputs}
						datas={data}
						boolean={boolean}
						setBoolean={setBoolean}
						imgPreview={imgPreview}
						setImgPreview={setImgPreview}
						setImgData={setImgData}
						isMyinfoSuccess={isMyinfoSuccess}
						setSchema={setSchema}
					/>
					<WorkExperience
						inputs={inputs}
						setInputs={setInputs}
						setCVData={setCVData}
						schema={schema}
						setSchema={setSchema}
					/>
					<AvailabilityStatus
						inputs={inputs}
						setInputs={setInputs}
						confirmPw={confirmPw}
						setConfirmPw={setConfirmPw}
						boolean={boolean}
						setBoolean={setBoolean}
						imgData={imgData}
						cvData={cvData}
						schema={schema}
						setSchema={setSchema}
						isNewProfile={isNewProfile}
					/>
				</Card.Body>
			</Card>
			<Modal
				show={showSingpassPrompt}
				size="lg"
				onHide={() => setShowSingpassPrompt(false)}
				centered
			>
				<Modal.Header closeButton className="border-0" />
				<Modal.Body className="text-center p-5 pt-0">
					<FontAwesomeIcon icon={faCircleInfo} className={styles.iconInfo} />
					<div className="mb-4">
						<strong>
                            Use Singpass to automatically update your profile
						</strong>
						<p>You can retrieve your personal information securely using Singpass for a faster profile setup.</p>
					</div>
					<Button
						className={`text-white ${techTalentStyles.primaryBtn} me-2`}
						type="button"
						size="lg"
						onClick={() => {
							setShowSingpassPrompt(false)
							window.location.href = `/api/singpass-auth/myinfo-login?type=${isNewProfile ? 'register' : 'update'}`
						}}
					>
                        Use Singpass
					</Button>
					<Button
						variant="outline-secondary"
						type="button"
						size="lg"
						onClick={() => setShowSingpassPrompt(false)}
					>
                        Fill Manually
					</Button>
				</Modal.Body>
			</Modal>
			<Modal
				show={isMyinfoFail}
				size="lg"
				onHide={() => setIsMyinfoFail(false)}
				centered
			>
				<Modal.Header closeButton className="border-0" />
				<Modal.Body className="text-center p-5 pt-0">
					<FontAwesomeIcon icon={faCircleInfo} className={styles.iconInfo} />
					<div className="mb-4">
						<strong>
							{t('register.singpass_myinfo_fail')}
						</strong>
					</div>
					<Button
						className={`text-white ${techTalentStyles.primaryBtn}`}
						type="button"
						size="lg"
						onClick={() => setIsMyinfoFail(false)}
					>
						{t('register.input_manual')}
					</Button>
				</Modal.Body>
			</Modal>
		</>
	)
}
